import { PostgrestClient } from "@supabase/postgrest-js";

import type { DatabaseWithOptions } from "../../../__definitions__/database-definitions";
import type { Claims } from "./get-claims";
import type { SupabaseHandlerArg, SupabaseSchemaName } from "./types";
import type { GetTokenSilentlyOptions, RedirectLoginOptions } from "@auth0/auth0-react";

export const getSupabase = <
  DB extends { db: unknown; options: { PostgrestVersion: string } },
  SchemaName extends SupabaseSchemaName<DB["db"]>,
>(
  url: string,
  key: string,
  access_token: string,
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  schema = "public" as SchemaName,
  abortController: AbortController,
): PostgrestClient<DB["db"], DB["options"], SchemaName> =>
  new PostgrestClient(`${url}/rest/v1`, {
    headers: {
      apikey: key,
      Authorization: `Bearer ${access_token}`,
    },
    schema,
    fetch: (furl, init) => fetch(furl, { ...init, signal: abortController.signal }),
  });

export async function withSupabaseToken<
  SchemaName extends SupabaseSchemaName<DatabaseWithOptions["db"]> = "public",
>(
  user: Claims | undefined,
  isLoading: boolean,
  isAuthenticated: boolean,
  getToken: (options?: GetTokenSilentlyOptions) => Promise<string | undefined>,
  loginWithRedirect: (options?: RedirectLoginOptions) => Promise<void>,
  schema: SchemaName,
  abortController: AbortController,
): Promise<SupabaseHandlerArg<SchemaName> | undefined> {
  if (isLoading) return undefined;
  if (!isAuthenticated) {
    await loginWithRedirect(
      globalThis.location.pathname.includes("/logout")
        ? {}
        : {
            appState: {
              returnTo: `${globalThis.location.pathname}${globalThis.location.search}`,
            },
          },
    );
    return undefined;
  }
  try {
    let token = await getToken();
    if (!user) return undefined;

    if ((user["http://changeengine.com/token_version"] ?? 1) < 2) {
      token = await getToken({ cacheMode: "off" });
    }

    if (token !== undefined) {
      return {
        supabase: getSupabase<DatabaseWithOptions, SchemaName>(
          user["http://changeengine.com/supabase_url"],
          user["http://changeengine.com/supabase_anon_key"],
          token,
          schema,
          abortController,
        ),
        account_id: user["http://changeengine.com/account_id"],
        org_id: user.org_id,
        token,
        abortController,
      };
    }
  } catch {
    // Fall through to the login redirect below.
  }
  await loginWithRedirect(
    globalThis.location.pathname.includes("/logout")
      ? {}
      : {
          appState: {
            returnTo: `${globalThis.location.pathname}${globalThis.location.search}`,
          },
        },
  );
  return undefined;
}
